/**
 * OsintExportButton — downloads a finished OSINT investigation as a branded PDF.
 *
 * Rides the generic `/v1/pdf/generate` endpoint via the shared `useGeneratePdf`
 * hook, so no OSINT-specific transport exists: the `osint_report` template is
 * just another registered template, and the document's letterhead, palette and
 * footer come from the shared PDF branding.
 *
 * Lives under `features/` rather than `components/` because it value-imports
 * react-query through the hook, which `check-frontend-rules.mjs` forbids inside
 * `src/components/**`.
 */

import { FileDown, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { useGeneratePdf } from '@/features/pdf-generation/hooks/use-generate-pdf'
import { resolveLanguage } from '@/services/i18n/languages'

interface Props {
  caseId: string
  osintId: string
  /** Disabled until a run has actually produced a report. */
  disabled?: boolean
}

export function OsintExportButton({ caseId, osintId, disabled = false }: Props) {
  const { t, i18n } = useTranslation()
  const generate = useGeneratePdf()

  const download = async () => {
    try {
      await generate.mutateAsync({
        template: 'osint_report',
        subjectRef: osintId,
        caseId,
        // Anything the template doesn't ship falls back to en server-side.
        locale: resolveLanguage(i18n.language),
      })
    } catch {
      toast.error(t('osint.export.error'))
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={download}
      disabled={disabled || generate.isPending}
    >
      {generate.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <FileDown className="size-4" />
      )}
      {t('osint.export.action')}
    </Button>
  )
}
